import React, { useState } from "react";
import axiosInstance from "../api/axiosInstance";
import clientAxiosInstance from "../api/clientAxiosInstance";
import { useToast } from "../context/ToastContext";
import { formatAccountName, isTwoOwnerAccount } from "../utils/formatters";
import ShareSlider, { SharePresetButtons } from "./ShareSlider";
import { RELATIONSHIP_OPTIONS } from "../constants/membersConstants";
import { isBeneficiaryShareTotalOver100 } from "../api/updateHouseholdAPI";
import { runRelationshipPrefill } from "../hooks/useRelationshipPrefill";
import { shouldPrefillRelationship } from "../hooks/relationshipPrefill";

function AddMemberBeneficiaryTrustedModal({ isOpen, onClose, household, newlyCreatedMemberId, onHouseholdUpdate, preSelectedAccountId = null, preSelectRole = null, clientToken = null }) {
  const { toast } = useToast();
  const [accountSelections, setAccountSelections] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (isOpen && preSelectedAccountId) {
      setAccountSelections({
        [preSelectedAccountId]: {
          isAccountOwner: false,
          isBeneficiary: preSelectRole === "beneficiary",
          beneficiaryType: "primary",
          beneficiaryShare: "",
          relationshipToAccountOwner: "",
          isTrustedContact: preSelectRole === "trusted_contact",
        }
      });
      if (preSelectRole === "beneficiary" || preSelectRole === "trusted_contact") {
        prefillRelationship(preSelectedAccountId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preSelectedAccountId, preSelectRole]);

  const prefillRelationship = (accountId) => {
    runRelationshipPrefill({
      householdId: household?.id,
      memberId: newlyCreatedMemberId,
      accountId,
      clientToken: clientToken || null,
      onPrefill: (relationship) =>
        setAccountSelections(prev => {
          const existing = prev[accountId] || {};
          if (!shouldPrefillRelationship(existing.relationshipToAccountOwner, relationship)) return prev;
          return {
            ...prev,
            [accountId]: { ...existing, relationshipToAccountOwner: relationship },
          };
        }),
    });
  };

  const handleBeneficiaryChange = (accountId, checked) => {
    setAccountSelections(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        isBeneficiary: checked,
        beneficiaryType: checked ? (prev[accountId]?.beneficiaryType || "primary") : "",
        beneficiaryShare: checked ? (prev[accountId]?.beneficiaryShare || "") : "",
      }
    }));
    setError("");
    if (checked) prefillRelationship(accountId);
  };

  const handleTrustedContactChange = (accountId, checked) => {
    setAccountSelections(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        isTrustedContact: checked,
      }
    }));
    setError("");
    if (checked) prefillRelationship(accountId);
  };

  const handleRelationshipToAccountOwnerChange = (accountId, value) => {
    setAccountSelections(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        relationshipToAccountOwner: value
      }
    }));
  };

  const handleBeneficiaryTypeChange = (accountId, type) => {
    setAccountSelections(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        beneficiaryType: type
      }
    }));
  };

  const handleBeneficiaryShareChange = (accountId, value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    setAccountSelections(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        beneficiaryShare: numValue
      }
    }));
  };

  const handleAccountOwnerChange = (accountId, checked) => {
    setAccountSelections(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        isAccountOwner: checked
      }
    }));
    setError("");
  };

  const isOwnerCheckboxEnabled = (account) => {
    const owners = account.owners_info || [];
    if (isTwoOwnerAccount(account)) {
      return owners.length < 2;
    }
    return owners.length < 1;
  };

  const validateSelections = () => {
    const allAccounts = household?.accounts || [];
    const memberIdStr = newlyCreatedMemberId != null ? String(newlyCreatedMemberId) : "";
    const householdMembers = household?.members || household?.household_members || [];
    const accountLabel = (acc) => {
      const idx = allAccounts.findIndex((a) => a?.id === acc?.id);
      const index1Based = idx >= 0 ? idx + 1 : 1;
      return formatAccountName(acc, index1Based, householdMembers);
    };

    for (const [, selection] of Object.entries(accountSelections)) {
      if (selection.isBeneficiary) {
        if (!selection.beneficiaryShare || selection.beneficiaryShare === "") {
          setError("Please enter a percentage share for all selected beneficiaries");
          return false;
        }
        const share = parseFloat(selection.beneficiaryShare);
        if (isNaN(share)) {
          setError("Percentage share must be a number");
          return false;
        }
        if (share < 0) {
          setError("Share % must be at least 0");
          return false;
        }
        if (share > 100) {
          setError("Share % must be at most 100");
          return false;
        }
        if (!selection.beneficiaryType) {
          setError("Please select a type (primary/contingent) for all beneficiaries");
          return false;
        }
      }
      if (selection.isBeneficiary || selection.isTrustedContact) {
        if (!selection.relationshipToAccountOwner) {
          setError("Please select a relationship to the account owner");
          return false;
        }
      }
    }

    // Duplicates: block selecting beneficiary/TC if already present on account
    for (const [accountId, selection] of Object.entries(accountSelections)) {
      const acc = allAccounts.find((a) => String(a.id) === String(accountId));
      if (!acc || !memberIdStr) continue;

      if (selection.isBeneficiary) {
        const alreadyBen = (acc.beneficiaries || []).some(
          (b) => b && String(b.household_member_id) === memberIdStr,
        );
        if (alreadyBen) {
          setError(
            `${accountLabel(acc)} already has this member as a beneficiary. Remove the duplicate before saving.`,
          );
          return false;
        }
      }

      if (selection.isTrustedContact) {
        const alreadyTc = (acc.trusted_contacts || []).some(
          (t) => t && String(t.household_member_id) === memberIdStr,
        );
        if (alreadyTc) {
          setError(
            `${accountLabel(acc)} already has this member as a trusted contact. Remove the duplicate before saving.`,
          );
          return false;
        }
      }
    }

    // Totals: block if primary/contingent totals would exceed 100% after adding this beneficiary
    for (const [accountId, selection] of Object.entries(accountSelections)) {
      if (!selection?.isBeneficiary) continue;
      const acc = allAccounts.find((a) => String(a.id) === String(accountId));
      if (!acc) continue;

      const newType = String(selection.beneficiaryType || "").toLowerCase();
      const newShare = parseFloat(selection.beneficiaryShare);
      const safeNewShare = Number.isFinite(newShare) ? newShare : 0;

      const existing = Array.isArray(acc.beneficiaries) ? acc.beneficiaries : [];
      const primaryTotal = existing.reduce((sum, b) => {
        const t = String(b?.type || "").toLowerCase();
        const sh = parseFloat(b?.share);
        const n = Number.isFinite(sh) ? sh : 0;
        return t === "primary" ? sum + n : sum;
      }, 0);
      const contingentTotal = existing.reduce((sum, b) => {
        const t = String(b?.type || "").toLowerCase();
        const sh = parseFloat(b?.share);
        const n = Number.isFinite(sh) ? sh : 0;
        return t === "contingent" ? sum + n : sum;
      }, 0);

      const nextPrimary = newType === "primary" ? primaryTotal + safeNewShare : primaryTotal;
      const nextContingent =
        newType === "contingent" ? contingentTotal + safeNewShare : contingentTotal;

      if (isBeneficiaryShareTotalOver100(nextPrimary)) {
        const roundPct = (n) => Math.round(n * 100) / 100;
        setError(
          `${accountLabel(acc)} primary beneficiary total would be ${roundPct(nextPrimary)}% (exceeds 100%). Reduce shares before saving.`,
        );
        return false;
      }
      if (isBeneficiaryShareTotalOver100(nextContingent)) {
        const roundPct = (n) => Math.round(n * 100) / 100;
        setError(
          `${accountLabel(acc)} contingent beneficiary total would be ${roundPct(nextContingent)}% (exceeds 100%). Reduce shares before saving.`,
        );
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateSelections()) return;

    // Build payload
    const accounts = [];
    const allAccounts = household?.accounts || [];
    for (const [accountId, selection] of Object.entries(accountSelections)) {
      if (!selection.isBeneficiary && !selection.isTrustedContact && !selection.isAccountOwner) continue;

      const update = {};

      if (selection.isAccountOwner) {
        const account = allAccounts.find(a => a.id === accountId);
        const existingOwners = (account?.owners_info || []).map(o => ({
          id: o.id,
          type: o.type || "primary",
        }));
        const newOwners = [...existingOwners, { id: newlyCreatedMemberId, type: "primary" }];
        const share = newOwners.length >= 2 ? 50 : 100;
        update.owners_info = newOwners.map(o => ({ ...o, share }));
      }

      if (selection.isBeneficiary) {
        update.add_beneficiaries = [{
          household_member_id: newlyCreatedMemberId,
          type: selection.beneficiaryType,
          share: parseFloat(selection.beneficiaryShare),
          relationship: selection.relationshipToAccountOwner,
        }];
      }

      if (selection.isTrustedContact) {
        update.add_trusted_contacts = [{
          household_member_id: newlyCreatedMemberId,
          relationship: selection.relationshipToAccountOwner,
        }];
      }

      accounts.push({
        account_id: accountId,
        update
      });
    }

    // If no accounts selected, just close and refresh
    if (accounts.length === 0) {
      if (onHouseholdUpdate) {
        onHouseholdUpdate(null);
      }
      handleClose();
      return;
    }

    setSaving(true);
    setError("");

    try {
      let response;
      
      // Use client API if clientToken is provided
      if (clientToken) {
        const clientPayload = {
          accounts: {}
        };

        accounts.forEach(acc => {
          const accountUpdate = {};

          if (acc.update.owners_info) {
            accountUpdate.owners_info = acc.update.owners_info;
          }
          
          if (acc.update.add_beneficiaries) {
            accountUpdate.beneficiaries = {
              create: acc.update.add_beneficiaries.map(b => ({
                household_member_id: b.household_member_id,
                type: b.type.toLowerCase(),
                share: b.share,
                relationship: b.relationship,
              }))
            };
          }
          
          if (acc.update.add_trusted_contacts) {
            accountUpdate.trusted_contacts = {
              create: acc.update.add_trusted_contacts
            };
          }
          
          clientPayload.accounts[acc.account_id] = accountUpdate;
        });

        response = await clientAxiosInstance.put(
          `/client/household/${household.id}`,
          clientPayload,
          {
            headers: {
              Authorization: `Bearer ${clientToken}`
            }
          }
        );
      } else {
        // Use advisor API (original behavior)
        response = await axiosInstance.put(
          `/households/${household.id}/accounts/bulk`,
          { accounts }
        );
      }

      if (response.data?.failed?.length) {
        toast.error("Some accounts failed to update. Please try again.");
      }

      // Use API response data directly
      if (onHouseholdUpdate) {
        onHouseholdUpdate(response.data);
      }

      handleClose();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(", "));
      } else {
        setError("Failed to update accounts. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setAccountSelections({});
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  const accounts = household?.accounts || [];
  const accountsToShow = preSelectedAccountId 
    ? accounts.filter(acc => acc.id === preSelectedAccountId)
    : accounts;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Link Member to Accounts</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={saving}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <p className="text-gray-700">
              Is this member an account owner, beneficiary, or trusted contact of any of the following accounts?
            </p>

            {accountsToShow.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-gray-600">No accounts found for this household.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {accountsToShow.map((account) => {
                  const accountId = account.id;
                  const selection = accountSelections[accountId] || {};
                  
                  // Get the actual index from the full accounts array
                  const actualIndex = accounts.findIndex(acc => acc.id === account.id);
                  const accountLabel = formatAccountName(account, actualIndex + 1, household?.members || []);

                  const ownerEnabled = isOwnerCheckboxEnabled(account);
                  const ownerCount = (account.owners_info || []).length;
                  const maxOwners = isTwoOwnerAccount(account) ? 2 : 1;

                  return (
                    <div key={accountId} className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <div className="font-semibold text-gray-800">
                        {accountLabel}
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label
                            htmlFor={`owner-${accountId}`}
                            className={`flex items-center gap-3 select-none ${
                              !ownerEnabled || saving
                                ? "opacity-60 pointer-events-none"
                                : "cursor-pointer"
                            }`}
                          >
                            <span className="relative flex shrink-0">
                              <input
                                type="checkbox"
                                id={`owner-${accountId}`}
                                checked={selection.isAccountOwner || false}
                                onChange={(e) => handleAccountOwnerChange(accountId, e.target.checked)}
                                className="sr-only peer"
                                disabled={!ownerEnabled || saving}
                              />
                              <span
                                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                                  !ownerEnabled
                                    ? "border-gray-200 bg-gray-100"
                                    : selection.isAccountOwner
                                      ? "bg-emerald-600 border-emerald-600"
                                      : "border-gray-300 bg-white hover:border-emerald-400 hover:bg-emerald-50/50"
                                }`}
                                aria-hidden
                              >
                                {selection.isAccountOwner && ownerEnabled && (
                                  <svg className="w-3.5 h-3.5 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                            </span>
                            <span className="flex-1">
                              <span className="font-medium text-gray-700">Account Owner</span>
                              {!ownerEnabled && (
                                <span className="ml-2 text-xs text-gray-400">
                                  ({ownerCount}/{maxOwners} owner{maxOwners > 1 ? "s" : ""} already assigned)
                                </span>
                              )}
                            </span>
                          </label>
                        </div>

                        <label
                          htmlFor={`beneficiary-${accountId}`}
                          className={`flex items-start gap-3 cursor-pointer select-none ${saving ? "opacity-60 pointer-events-none" : ""}`}
                        >
                          <span className="relative flex shrink-0 mt-0.5">
                            <input
                              type="checkbox"
                              id={`beneficiary-${accountId}`}
                              checked={selection.isBeneficiary || false}
                              onChange={(e) => handleBeneficiaryChange(accountId, e.target.checked)}
                              className="sr-only peer"
                              disabled={saving}
                            />
                            <span
                              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                                selection.isBeneficiary
                                  ? "bg-emerald-600 border-emerald-600"
                                  : "border-gray-300 bg-white hover:border-emerald-400 hover:bg-emerald-50/50"
                              }`}
                              aria-hidden
                            >
                              {selection.isBeneficiary && (
                                <svg className="w-3.5 h-3.5 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          </span>
                          <span className="flex-1">
                            <span className="font-medium text-gray-700">Beneficiary</span>
                            {selection.isBeneficiary && (
                              <div className="mt-2 grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Type <span className="text-red-500">*</span>
                                  </label>
                                  <select
                                    value={selection.beneficiaryType || "primary"}
                                    onChange={(e) => handleBeneficiaryTypeChange(accountId, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    disabled={saving}
                                  >
                                    <option value="primary">Primary</option>
                                    <option value="contingent">Contingent</option>
                                  </select>
                                </div>
                                <div>
                                  <div className="flex items-center gap-4 mb-1">
                                    <label className="text-sm font-medium text-gray-700">
                                      Share (%) <span className="text-red-500">*</span>
                                    </label>
                                    <SharePresetButtons
                                      value={selection.beneficiaryShare || 0}
                                      onChange={(val) => handleBeneficiaryShareChange(accountId, val)}
                                      disabled={saving}
                                    />
                                  </div>
                                  <ShareSlider
                                    value={selection.beneficiaryShare || 0}
                                    onChange={(val) => handleBeneficiaryShareChange(accountId, val)}
                                    disabled={saving}
                                  />
                                </div>
                              </div>
                            )}
                          </span>
                        </label>

                        <label
                          htmlFor={`trusted-${accountId}`}
                          className={`flex items-center gap-3 cursor-pointer select-none ${saving ? "opacity-60 pointer-events-none" : ""}`}
                        >
                          <span className="relative flex shrink-0">
                            <input
                              type="checkbox"
                              id={`trusted-${accountId}`}
                              checked={selection.isTrustedContact || false}
                              onChange={(e) => handleTrustedContactChange(accountId, e.target.checked)}
                              className="sr-only peer"
                              disabled={saving}
                            />
                            <span
                              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                                selection.isTrustedContact
                                  ? "bg-emerald-600 border-emerald-600"
                                  : "border-gray-300 bg-white hover:border-emerald-400 hover:bg-emerald-50/50"
                              }`}
                              aria-hidden
                            >
                              {selection.isTrustedContact && (
                                <svg className="w-3.5 h-3.5 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          </span>
                          <span className="flex-1">
                            <span className="font-medium text-gray-700">Trusted Contact</span>
                          </span>
                        </label>

                        {(selection.isBeneficiary || selection.isTrustedContact) && (
                          <div className="mt-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Relationship to Account Owner <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={selection.relationshipToAccountOwner || ""}
                              onChange={(e) =>
                                handleRelationshipToAccountOwnerChange(accountId, e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                              disabled={saving}
                            >
                              <option value="">Select relationship to account owner</option>
                              {RELATIONSHIP_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddMemberBeneficiaryTrustedModal;
